// Tracker leggero page-view + dwell time per la SPA (best-effort, zero dipendenze).
// Pattern: sendBeacon su pagehide/visibilitychange, idle sessione, visitorId persistente.
import { TELEMETRY_URL } from '../utils/api';

const ENDPOINT = `${TELEMETRY_URL}/api/telemetry/view`;
const SESSION_IDLE_MS = 30 * 60 * 1000;
const TICK_MS = 15000;
const MAX_DWELL_MS = 2 * 60 * 60 * 1000;

function uid(): string {
  try { return crypto.randomUUID(); } catch { return `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`; }
}

function visitorId(): string {
  try {
    let v = localStorage.getItem('jv_vid');
    if (!v) { v = uid(); localStorage.setItem('jv_vid', v); }
    return v;
  } catch { return 'anon'; }
}

function sessionId(): string {
  try {
    const now = Date.now();
    const raw = sessionStorage.getItem('jv_sess');
    if (raw) {
      const s = JSON.parse(raw) as { id: string; at: number };
      if (now - s.at < SESSION_IDLE_MS) { s.at = now; sessionStorage.setItem('jv_sess', JSON.stringify(s)); return s.id; }
    }
    const id = uid();
    sessionStorage.setItem('jv_sess', JSON.stringify({ id, at: now }));
    return id;
  } catch { return 'anon'; }
}

function send(payload: Record<string, unknown>): void {
  const body = JSON.stringify(payload);
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      if (navigator.sendBeacon(ENDPOINT, blob)) return;
    }
  } catch { /* fallback sotto */ }
  try {
    fetch(ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
  } catch { /* best-effort */ }
}

interface CurrentPath { path: string; activeMs: number; lastTick: number }
let current: CurrentPath = { path: '', activeMs: 0, lastTick: 0 };
// Gating consenso: quando false nessun evento viene emesso (best-effort stop).
let active = false;
// Setup una-tantum: monkeypatch di history e listener installati una sola volta, inerti
// quando !active. Cosi' stop -> init (ri-consenso) non duplica patch/listener.
let installed = false;
let lastUrl = '';

function emit(path: string, dwellMs: number): void {
  if (!path || !active) return;
  send({
    path,
    referrer: document.referrer || null,
    dwellMs: Math.min(Math.round(dwellMs), MAX_DWELL_MS),
    sessionId: sessionId(),
    fingerprint: visitorId(),
    lang: document.documentElement.lang || null,
  });
}

function closeCurrent(): void {
  if (!current.path) return;
  const live = document.visibilityState === 'visible' ? Date.now() - current.lastTick : 0;
  emit(current.path, current.activeMs + live);
  current = { path: '', activeMs: 0, lastTick: 0 };
}

function norm(p: string): string {
  return p.replace(/^\/[a-zA-Z]{2}(-[a-zA-Z]{2})?(?=\/|$)/, '').replace(/\/+$/, '') || '/';
}

function enterPath(path: string): void {
  if (current.path && norm(current.path) === norm(path)) {
    current.path = path;
    return;
  }
  closeCurrent();
  current = { path, activeMs: 0, lastTick: Date.now() };
  emit(path, 0);
}

function install(): void {
  if (installed) return;
  installed = true;
  lastUrl = location.pathname + location.search;

  const currentUrl = () => location.pathname + location.search;
  let pending: ReturnType<typeof setTimeout> | undefined;
  const onNav = () => {
    if (!active) return;
    const p = currentUrl();
    if (p === lastUrl) return;
    lastUrl = p;
    clearTimeout(pending);
    // Debounce: le navigazioni transitorie del router (path cambia e ritorna)
    // non devono generare una page-view fantasma.
    pending = setTimeout(() => { if (active && currentUrl() === p) enterPath(p); }, 500);
  };
  for (const m of ['pushState', 'replaceState'] as const) {
    const orig = history[m];
    history[m] = function (this: History, ...args: unknown[]) {
      (orig as (...a: unknown[]) => void).apply(this, args);
      onNav();
    } as History[typeof m];
  }
  window.addEventListener('popstate', onNav);

  const tick = setInterval(() => {
    if (!active || document.visibilityState !== 'visible') return;
    const now = Date.now();
    current.activeMs += now - current.lastTick;
    current.lastTick = now;
  }, TICK_MS);

  const onVisibility = () => {
    if (document.visibilityState === 'hidden') { closeCurrent(); }
    else if (current.path) { current.lastTick = Date.now(); }
  };
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', closeCurrent);
  void tick;
}

export function initPageViewTracking(): void {
  if (typeof window === 'undefined') return;
  if (import.meta.env.DEV) return;
  // Solo web: l'app Electron usa file:// + HashRouter, il pathname non e' una rotta utile.
  if (window.location.protocol === 'file:') return;
  install();
  if (active) return; // gia' attivo
  try {
    active = true;
    enterPath(location.pathname + location.search);
  } catch {
    active = false;
  }
}

// Best-effort stop (consenso revocato): chiude la vista corrente e smette di emettere.
// I monkeypatch di history e i listener restano installati ma inerti (guard `active`),
// quindi un successivo initPageViewTracking() li riusa e riparte senza duplicarli.
export function stopPageViewTracking(): void {
  if (!active) return;
  active = false;
  try { closeCurrent(); } catch { /* no-op */ }
}
